/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { SKELETON_BLOCK, skeletonSlots } from './skeleton-primitives';

export interface CardListSkeletonProps {
  /** Number of card placeholders to render. */
  count?: number;
  /** Render a leading circular avatar placeholder per card. */
  withAvatar?: boolean;
}

/**
 * Placeholder mimicking a vertical list of cards (avatar + two text lines).
 *
 * @param props Card count and whether each card shows an avatar.
 * @returns A shimmering card-list placeholder.
 */
export const CardListSkeleton: React.FC<CardListSkeletonProps> = ({
  count = 3,
  withAvatar = false,
}) => (
  <div data-testid="card-list-skeleton" className="flex flex-col gap-3">
    {skeletonSlots(count).map((slot) => (
      <div
        key={slot}
        data-testid="card-list-skeleton-item"
        className="flex items-center gap-4 rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-5"
      >
        {withAvatar && (
          <div
            data-testid="card-list-skeleton-avatar"
            className={`${SKELETON_BLOCK} h-12 w-12 rounded-full`}
          />
        )}
        <div className="flex flex-1 flex-col gap-2">
          <div className={`${SKELETON_BLOCK} h-4 w-1/3`} />
          <div className={`${SKELETON_BLOCK} h-3 w-1/4`} />
        </div>
      </div>
    ))}
  </div>
);
